var _ = require('optimizely-client-fn');
var hashing = require('optimizely-hashing');
var JSONEnv = require('core/lib/json');
var di = require('core/di');
var uuid = require('optimizely-uuid');
var Visitor = require('core/visitor');
var Logger = require('core/utils/logger');
var enums = require('core/constants/enums');

var alias = require('plugins/feature_dcp/helpers/alias');

var VisitorStore = di.get('stores/visitor');
var PluginsStore = di.get('stores/plugins');
var GlobalStore = di.get('stores/global');

var VARIATION_TIMEOUT_30_MINUTES = 30 * 60 * 1000;
var VARIATION_TIMEOUT_24_HOURS = 24 * 60 * 60 * 1000;

exports.generateCmabUUID = () => uuid.generate();

exports.getCmabLayerIdFromExperiment = function (experiment: Experiment): string {
    return experiment.experimentMetadata && experiment.experimentMetadata.layerId || experiment.id;
};

exports.isCmabExperiment = function (experiment: Experiment): boolean {
    var allocationPolicy = experiment.experimentMetadata && experiment.experimentMetadata.allocationPolicy;
    if (!allocationPolicy) {
        Logger.debug('No allocation policy found in metadata for experiment:', experiment.id);
        return false;
    }
    return allocationPolicy === enums.AllocationPolicyEnum.CMAB;
};

exports.isCmabTimeout = function (experiment: Experiment, cmabVariationData: CmabVariationStorageData): boolean {
    if (__CMAB_OVERRIDE_SESSION_TIMEOUT__) {
        Logger.debug('CMAB / Timeout Session timeout override (__CMAB_OVERRIDE_SESSION_TIMEOUT__) is enabled. Ignoring session timeout and forcing a prediction API call to fetch live variation from the ML model.');
        return true;
    }

    if (!exports.isCmabVariationStorageDataValid(cmabVariationData)) {
        Logger.debug('CMAB / Timeout CMAB Storage Variation Data is invalid');
        return true;
    }

    var cmabVariationId = cmabVariationData.variationId;

    // Check if the variation is still part of the experiment
    if (!_.find(experiment.variations, { id: cmabVariationId })) {
        Logger.debug('CMAB / Timeout Variation ID:', cmabVariationId, 'not found in experiment:', experiment.id);
        return true;
    }

    // Check if the timeout flag is set
    if (cmabVariationData.isTimeout) {
        Logger.debug('CMAB / Timeout flag is set:', cmabVariationData.cmabUUID);
        return true;
    }

    // Check if user attributes have changed
    if (exports.hasAttributesChanged(experiment, cmabVariationData)) {
        Logger.debug('CMAB / Timeout User attributes have changed');
        return true;
    }

    // Check if the timeout has happened for 30 minutes or 24 hours
    if (exports.has30MinOr24HrTimeoutOccurred(cmabVariationData)) {
        Logger.debug('CMAB / Timeout for 30 minutes or 24 hours:', cmabVariationData.cmabUUID);
        return true;
    }


    Logger.debug('CMAB / No timeout happened for:', cmabVariationData.cmabUUID);
    return false;
};

exports.has30MinOr24HrTimeoutOccurred = function (cmabVariationData: CmabVariationStorageData): boolean {
    if (!exports.isCmabVariationStorageDataValid(cmabVariationData)) {
        Logger.debug('CMAB / Timeout CMAB Storage Variation Data is invalid');
        return true;
    }

    var currentTime = new Date().getTime();
    var st30MinTimeout = new Date(cmabVariationData.st30MinTimeout).getTime();
    var st24HrTimeout = new Date(cmabVariationData.st24HrTimeout).getTime();

    // Check if the timeout has happened for 30 minutes or 24 hours
    if (currentTime - st30MinTimeout > VARIATION_TIMEOUT_30_MINUTES) {
        return true;
    } else if (currentTime - st24HrTimeout > VARIATION_TIMEOUT_24_HOURS) {
        return true;
    }

    return false;
};

exports.isCmabVariationStorageDataValid = function (cmabVariationData: CmabVariationStorageData): boolean {
    if (!cmabVariationData) {
        return false;
    }

    const requiredFields = [
        'variationId',
        'visitorId',
        'cmabUUID',
        'st30MinTimeout',
        'st24HrTimeout',
        'userAttributeHash',
    ];

    for (const field of requiredFields) {
        if (!cmabVariationData[field]) {
            Logger.debug(`CMAB / Storage data not found: ${field} is missing`);
            return false;
        }
    }
    return true;
};

exports.activateTimeoutForNonBucketedCmabExperiments = function (experiment: Experiment): void {
    var layerId = exports.getCmabLayerIdFromExperiment(experiment);
    var experimentId = experiment.id;
    Logger.debug('CMAB / Visitor is bucketed into experiment id:', experimentId + ' from layer id:', layerId);
    var contextualMabMap = Visitor.getContextualMabMap();
    if (!contextualMabMap || !contextualMabMap[layerId]) {
        Logger.debug('CMAB / No CMAB experiments found for the current layer id:', layerId);
        return;
    }

    var cmabExperiments = contextualMabMap[layerId];
    Logger.debug('CMAB / All CMAB experiments found for the layer id:', layerId, 'are:', cmabExperiments);
    Logger.debug('CMAB / Excluding the currently bucketed experiment id: ', experimentId, ' from all CMAB experiments found for the current layer id: ', layerId);
    var nonBucketedCmabExperiments = _.omit(cmabExperiments, experimentId);

    // Remove the cmabExperiment where isTimeout is true
    nonBucketedCmabExperiments = _.omitBy(nonBucketedCmabExperiments, (cmabVariationData) => cmabVariationData.isTimeout);

    Logger.debug('CMAB / Non-bucketed CMAB experiments which will be timed out:', nonBucketedCmabExperiments);
    _.forEach(nonBucketedCmabExperiments, function (cmabVariationData, cmabExperimentId) {
        cmabVariationData.isTimeout = true;
        Visitor.updateContextualMabMap(layerId, cmabExperimentId, cmabVariationData);
    });
    Visitor.persistContextualMabMap();
};

exports.getCmabUUIDFromVisitorStore = function (layerId: string, experimentId: string): string {
    if (!experimentId || !layerId) return null;
    var contextualMabMap = Visitor.getContextualMabMap();
    if (!contextualMabMap || !contextualMabMap[layerId] || !contextualMabMap[layerId][experimentId]) return null;
    var contextualMabVariationData = contextualMabMap[layerId][experimentId] as CmabVariationStorageData;
    return contextualMabVariationData.cmabUUID;
};

exports.updateCmabTimeoutInVisitorStore = function (layerId: string, experimentId: string): void {
    if (!experimentId || !layerId) return null;
    var contextualMabMap = Visitor.getContextualMabMap();
    if (!contextualMabMap?.[layerId]?.[experimentId]) return;
    var contextualMabVariationData = contextualMabMap[layerId][experimentId] as CmabVariationStorageData;

    if (contextualMabVariationData.isTimeout || exports.has30MinOr24HrTimeoutOccurred(contextualMabVariationData)) {
        return;
    }

    // Update the timeout for 30 minutes timeout when a event is sent to logx endpoint
    contextualMabVariationData.st30MinTimeout = new Date().toISOString();
    Visitor.updateContextualMabMap(layerId, experimentId, contextualMabVariationData);
    Visitor.persistContextualMabMap();
};

exports.getCmabVariationDataFromVisitorStore = function (experiment: Experiment): CmabVariationStorageData {
    var layerId = exports.getCmabLayerIdFromExperiment(experiment);
    var contextualMabMap = Visitor.getContextualMabMap();
    if (!contextualMabMap?.[layerId]?.[experiment.id]) {
        Logger.debug('CMAB / No CMAB store found for experiment:', experiment.id);
        return null;
    }

    Logger.debug('CMAB / CMAB store found for experiment:', experiment.id);
    var contextualMabVariationData = contextualMabMap[layerId][experiment.id];
    Logger.debug('CMAB / Contextual MAB Variation Data:', contextualMabVariationData);
    return contextualMabVariationData;
};

exports.setCmabVariationDataInVisitorStore = function (experiment: Experiment, variationId: string, cmabUUID: string): void {
    Logger.debug('CMAB / Setting CMAB store for experiment:', experiment.id + ' with variation:', variationId + ' and cmabUUID:', cmabUUID);
    var cmabCurrentTime = new Date().toISOString();
    var layerId = exports.getCmabLayerIdFromExperiment(experiment);
    var hash = exports.getVisitorAttributesHash(experiment);

    var contextualMabVariationData: CmabVariationStorageData = {
        variationId: variationId,
        visitorId: Visitor.getCurrentId(),
        cmabUUID: cmabUUID,
        st30MinTimeout: cmabCurrentTime,
        st24HrTimeout: cmabCurrentTime,
        userAttributeHash: hash,
        isTimeout: false,
    };
    Visitor.updateContextualMabMap(layerId, experiment.id, contextualMabVariationData);
    Visitor.persistContextualMabMap();
};

exports.getStringifiedValue = function (value: any): string {
    if (typeof value === 'string') return value;
    var valueStr = '';
    try {
        valueStr = JSONEnv.stringify(value);
    } catch (error) {
        Logger.error('CMAB / Failed to stringify value for attributes:', value, error);
    }
    return valueStr;
};

exports.getVisitorCmabAttributesForExperiment = function (experiment: Experiment): CmabAttributeValue[] {
    var attributes: CmabAttributeValue[] = [];
    var matchersMap = PluginsStore.getAllPlugins(enums.PluginTypes.audienceMatchers);
    if (!experiment?.experimentMetadata?.cmabAttributes) {
        return attributes;
    }
    var { customAttributes, standardAttributes } = experiment.experimentMetadata.cmabAttributes;
    _.forEach(customAttributes, (attribute: CustomCmabAttribute) => {
        var value = VisitorStore.getAttribute(['custom', attribute.id])?.value || '';
        value = exports.getStringifiedValue(value);
        attributes.push({ id: attribute.id, type: attribute.type, value: value, client_api_name: attribute.clientApiName });
    });

    _.forEach(standardAttributes, (attribute: StandardCmabAttribute) => {
        var key = matchersMap[attribute.id]?.fieldsNeeded?.[0] || attribute.id;
        var value = VisitorStore.getAttribute(key) || '';
        value = exports.getStringifiedValue(value);
        attributes.push({ id: attribute.id, type: attribute.type, value: value });
    });

    Logger.debug('CMAB / Current Visitor CMAB Attributes for Experiment:', experiment.id, 'are:', attributes);
    return attributes;
};

exports.generateAttributesHash = function (attributes: CmabAttributeValue[]): string {
    var hash = hashing.hashToHex(hashing.toByteString(JSONEnv.stringify(attributes)), hashing.Seed.BEHAVIOR_EVENT);
    return hash;
};

exports.getVisitorAttributesHash = function (experiment: Experiment): string {
    var attributes = exports.getVisitorCmabAttributesForExperiment(experiment);
    return exports.generateAttributesHash(attributes);
};

exports.hasAttributesChanged = function (experiment: Experiment, cmabVariationData: CmabVariationStorageData): boolean {
    var hash = exports.getVisitorAttributesHash(experiment);
    if (hash !== cmabVariationData.userAttributeHash) {
        Logger.debug('CMAB / User attributes hash has changed from:', cmabVariationData.userAttributeHash, ' to ', hash);
        return true;
    }
    return false;
};

exports.getDcpCustomerIds = function () {
    var keyfieldLocators = GlobalStore.getDCPKeyfieldLocators();
    var visitorId = Visitor.getCurrentId();
    var aliases = alias.getCurrentAliases(keyfieldLocators, visitorId);
    return aliases;
};

exports.getDcpServiceId = function () {
    return GlobalStore.getDCPServiceId();
};
